/**
 * Loading the Clerk widget.
 *
 * The old implementation polled `window.Clerk` every 200ms forever. If the
 * script never arrived — blocked, offline, misconfigured hostname, a deleted
 * Clerk tenant — the dashboard sat on "signing in…" permanently with nothing
 * in the console. Every path here either resolves or rejects.
 */

export interface ClerkOrganization {
  id: string;
  name?: string;
}

export interface ClerkMembership {
  organization: ClerkOrganization;
}

export interface ClerkUser {
  id: string;
  firstName?: string | null;
  primaryEmailAddress?: { emailAddress?: string } | null;
  organizationMemberships?: ClerkMembership[];
}

export interface ClerkGlobal {
  load(): Promise<void>;
  user?: ClerkUser | null;
  /** The active organization, or null until one is selected/created. Every
   *  API call needs its id in the JWT — see ensureActiveOrg. */
  organization?: ClerkOrganization | null;
  session?: {
    getToken(options?: { skipCache?: boolean }): Promise<string | null>;
  } | null;
  openSignIn(options?: Record<string, unknown>): void;
  signOut(): Promise<void>;
  createOrganization(params: { name: string }): Promise<ClerkOrganization>;
  setActive(params: { organization: string }): Promise<void>;
  addListener?(fn: (payload: { user?: ClerkUser | null }) => void): void;
  mountUserButton?(node: HTMLElement): void;
}

declare global {
  interface Window {
    Clerk?: ClerkGlobal;
  }
}

export const CLERK_LOAD_TIMEOUT_MS = 15_000;

export class ClerkLoadError extends Error {
  constructor(message: string, override readonly cause?: unknown) {
    super(message);
    this.name = 'ClerkLoadError';
  }
}

let loader: Promise<ClerkGlobal> | null = null;

/** Inject the Clerk script and resolve once `window.Clerk` is usable. */
export function loadClerk(
  publishableKey: string,
  scriptUrl: string,
  timeoutMs = CLERK_LOAD_TIMEOUT_MS,
): Promise<ClerkGlobal> {
  if (loader) return loader;

  loader = new Promise<ClerkGlobal>((resolve, reject) => {
    if (!publishableKey) {
      reject(new ClerkLoadError('No Clerk publishable key was configured.'));
      return;
    }
    if (window.Clerk) {
      resolve(window.Clerk);
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = scriptUrl;
    script.dataset.clerkPublishableKey = publishableKey;

    const timer = window.setTimeout(() => {
      cleanup();
      reject(
        new ClerkLoadError(
          `Clerk did not load within ${Math.round(timeoutMs / 1000)}s. ` +
            'Check the sign-in host is reachable.',
        ),
      );
    }, timeoutMs);

    function cleanup(): void {
      window.clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
    }

    script.onload = () => {
      cleanup();
      if (!window.Clerk) {
        reject(new ClerkLoadError('Clerk script loaded but exposed no client.'));
        return;
      }
      resolve(window.Clerk);
    };
    script.onerror = (event) => {
      cleanup();
      reject(new ClerkLoadError(`Could not fetch the Clerk script from ${scriptUrl}.`, event));
    };

    document.head.appendChild(script);
  });

  // A failed load must not be cached, or a transient network blip would
  // wedge sign-in until a full page reload.
  loader.catch(() => {
    loader = null;
  });

  return loader;
}

/** Test seam. */
export function resetClerkLoader(): void {
  loader = null;
}


/** A readable organization name for a first-time user, mirroring the Clerk
 *  instance template ("{{user.first_name}}'s Organization") with sane
 *  fallbacks — a table is a tenant, and it should not be called "Untitled". */
export function defaultOrgName(user: ClerkUser | null | undefined): string {
  const first = user?.firstName?.trim();
  if (first) return `${first}'s table`;
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const local = email.split('@')[0]?.trim();
  if (local) return `${local}'s table`;
  return 'My table';
}

/**
 * Make sure the signed-in user has an ACTIVE organization.
 *
 * The whole app is tenant-scoped, and the tenant comes from the org id in the
 * Clerk JWT: `verify_clerk_token` rejects any request whose token lacks one.
 * Clerk's instance requires an org but does not create one automatically, and
 * the sign-in modal we open completes authentication without running the org
 * step — so a brand-new account arrived signed-in with no org, every API call
 * 401'd, and the client read that as "session expired" and bounced them back
 * to sign-in. They never reached the screen that starts the trial.
 *
 * This closes that gap on the client, where the active org actually lives:
 * reuse an existing membership if the user has one (an invited co-GM), else
 * create a personal org. Either way we set it active and force a fresh token,
 * so the next API call carries the org id.
 *
 * Idempotent: a returning user already has an active org and this is a single
 * property read.
 */
// Single-flight per Clerk instance. Boot awaits this AND the auth listener
// fires it (Clerk emits current state on subscribe), so without dedup two
// concurrent runs each saw "no org" and each created one — a real user ended
// up with two identical orgs. A failed run clears the slot so retry works.
const orgSettles = new WeakMap<ClerkGlobal, Promise<void>>();

export function ensureActiveOrg(clerk: ClerkGlobal): Promise<void> {
  const inFlight = orgSettles.get(clerk);
  if (inFlight) return inFlight;
  const run = ensureActiveOrgOnce(clerk).catch((err) => {
    orgSettles.delete(clerk);
    throw err;
  });
  orgSettles.set(clerk, run);
  return run;
}

async function ensureActiveOrgOnce(clerk: ClerkGlobal): Promise<void> {
  if (!clerk.user) return;
  if (clerk.organization) {
    // Force-org sessions can arrive from Clerk's hosted flow still marked
    // "pending" even with an org active. The token carries the org id and
    // the edge is the real arbiter, so refresh it and move on rather than
    // waiting for task bookkeeping that may never settle here.
    await clerk.session?.getToken({ skipCache: true });
    return;
  }

  const existing = clerk.user.organizationMemberships ?? [];
  const membership = existing[0];
  if (membership) {
    await clerk.setActive({ organization: membership.organization.id });
  } else {
    const org = await clerk.createOrganization({
      name: defaultOrgName(clerk.user),
    });
    await clerk.setActive({ organization: org.id });
  }

  // The token is cached per session; without this the first request after
  // sign-in can still carry the pre-org token and 401.
  await clerk.session?.getToken({ skipCache: true });
}
